import { UnitConversion } from '../inputs/UnitConversion';

export default function DownloadSummaryPdf({
  inputs,
  result,
  machineSummary,
  priceValue,
  options,
}) {
  /** -----------------------------
   *  Utility helpers
   * ------------------------------*/
  const round = (value, decimal = 2) =>
    value || value === 0 ? Number(value).toFixed(decimal) : '';

  const create = (tag, styles = {}, text = '') => {
    const el = document.createElement(tag);
    Object.assign(el.style, styles);
    if (text) el.innerText = text;
    return el;
  };

  const appendChildren = (parent, children = []) =>
    children.forEach((c) => parent.appendChild(c));

  const createSectionTitle = (text) => {
    const h = create('h3', {
      borderBottom: '1px solid #ddd',
      paddingBottom: '6px',
      marginTop: '25px',
    });
    h.innerText = text;
    return h;
  };

  const createParagraph = (text) =>
    create('p', { fontSize: '12px', color: 'rgb(19, 44, 77)' }, text);

  const createTableRow = (row, parent) => {
    const tr = document.createElement('tr');

    const label = create(
      'td',
      { fontWeight: '600', borderBottom: '1px solid #eee', padding: '6px 8px' },
      row.label
    );

    const value = create('td', {
      borderBottom: '1px solid #eee',
      padding: '6px 8px',
    });

    if (row.label === 'In-Out configuration') {
      const img = create('img', {
        height: '42px',
        objectFit: 'contain',
      });
      img.src = `${process.env.PUBLIC_URL}/pics/configuration/config${inputs.configuration}.png`;
      img.alt = `Configuration ${inputs.configuration}`;
      value.appendChild(img);
    } else {
      value.innerText = row.value ?? '';
    }

    appendChildren(tr, [label, value]);
    parent.appendChild(tr);
  };

  /** -----------------------------
   *  Main container
   * ------------------------------*/
  const root = create('div', {
    padding: '24px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: 'rgb(19, 44, 77)',
    fontSize: '12px',
    lineHeight: '1.4',
    width: '800px',
  });
  root.id = 'print-summary-container';

  /** -----------------------------
   * Header
   * ------------------------------*/
  const title = create('h2', { marginTop: '0' }, 'Budget pricing');

  const subtitle = create('div', {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  });

  appendChildren(subtitle, [
    create(
      'div',
      {},
      inputs.crm_info?.opportunity_number
        ? inputs.crm_info.opportunity_number
        : inputs.crm_info?.budget?.name
          ? inputs.crm_info.budget.name
          : ''
    ),
    create('div', {}, new Date().toLocaleString()),
  ]);

  const revision = create('div', {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  });

  appendChildren(revision, [
    create(
      'div',
      {},
      inputs.crm_info?.budget?.revision
        ? `revision: ${inputs.crm_info?.budget?.revision}`
        : ''
    ),
  ]);

  appendChildren(root, [title, subtitle, revision]);

  /** -----------------------------
   * Price section
   * ------------------------------*/
  root.appendChild(createSectionTitle('Price'));

  const priceP = create(
    'p',
    { fontWeight: 'bold', fontSize: '14px' },
    `Machine gross price: ${priceValue}`
  );
  root.appendChild(priceP);

  /** -----------------------------
   * Machine section
   * ------------------------------*/
  root.appendChild(createSectionTitle('Machine specifications'));

  const machineTable = create('table', {
    borderCollapse: 'collapse',
    width: '100%',
  });
  machineTable.classList.add('shared-table');

  const heightData = ['h1', 'h2', 'h3'].map((h) => {
    const option = options.find((o) => o.field === h);
    const name = option.name.replace(/\s*\[.*?\]\s*/g, '').trim();
    const unitMatch = option.name.match(/\[(.*?)\]/);
    const baseUnit = unitMatch ? unitMatch[1] : '';
    const rawValue = result?.[h] ?? inputs?.[h];
    const { displayedValue, displayedUnit } = UnitConversion(
      rawValue,
      inputs.unit_system,
      baseUnit
    );

    return {
      label: name,
      value:
        displayedValue || displayedValue === 0
          ? `${round(displayedValue, 0)} ${displayedUnit}`
          : '',
    };
  });

  const machineData = [
    ...heightData,
    ...machineSummary.filter((row) => row.label !== 'Outer diameter'),
  ];

  machineData.forEach((row) => {
    createTableRow(row, machineTable);
  });

  root.appendChild(machineTable);

  /** -----------------------------
   * Capacity section
   * ------------------------------*/
  root.appendChild(createSectionTitle('Capacity'));

  const normalizeVal = (v) => {
    if (!v) return 0;
    if (typeof v === 'object') return Number(v[Object.keys(v)[0]]) || 0;
    if (typeof v === 'string') return Number(v.replace('%', '')) || 0;
    return round(Number(v), 0) || 0;
  };

  const capacity = [
    ['max_allowed_load_line', 'kg/m'],
    ['max_total_load', 'kg'],
    ['torque_rating', 'Nm'],
  ].map(([maxField, baseUnit]) => {
    const rawValue = normalizeVal(result?.[maxField]);
    const { displayedValue, displayedUnit } = UnitConversion(
      rawValue,
      inputs.unit_system,
      baseUnit
    );
    return {
      label:
        options?.find((o) => o.field === maxField)?.name.split('[')[0] ??
        maxField.replace(/_/g, ' '),
      value:
        displayedValue || displayedValue === 0
          ? `${round(displayedValue, 0)} ${displayedUnit}`
          : '',
    };
  });

  const capacityTable = create('table', {
    borderCollapse: 'collapse',
    width: '100%',
  });
  capacityTable.classList.add('shared-table');

  const cubicBaseValues = [
    result.product_max_length,
    result.horizontal_clearance_without_product,
    result.vertical_clearance_without_product,
  ];

  const cubicDisplay = cubicBaseValues.map((v) =>
    UnitConversion(v, inputs.unit_system, 'mm')
  );

  const cubicUnit = cubicDisplay[0].displayedUnit;

  const cubicValue = cubicDisplay
    .map((v) => round(v.displayedValue, 0))
    .join(' x ');

  const capacityData = [
    {
      label: 'Cubic Space available',
      value: cubicValue ? `${cubicValue} ${cubicUnit}` : '',
    },
    ...capacity,
  ];

  capacityData.forEach((row) => {
    createTableRow(row, capacityTable);
  });

  root.appendChild(capacityTable);

  /** -----------------------------
   * Disclaimer
   * ------------------------------*/
  root.appendChild(createSectionTitle('Disclaimer'));
  root.appendChild(
    createParagraph(
      'The budget price provided is an estimate and is not guaranteed. It is based solely on the information supplied by the customer regarding the project requirements. Actual costs, equipment implementation, and lead times may vary due to additional details, testing, or changes in specifications.'
    )
  );
  root.appendChild(
    createParagraph(
      'While we aim for accuracy in our budget estimates, it is important to understand that the final price may differ from the initial estimate. Additionally, this budget price should not be regarded as a formal quotation or legally binding agreement. It is intended strictly for informational use and to serve as a foundation for discussion and further Collaboration.'
    )
  );

  /** -----------------------------
   * Print wrapper + styles
   * ------------------------------*/
  const printWrapper = create('div');
  printWrapper.id = 'print-area-wrapper';

  const printStyles = document.createElement('style');
  printStyles.innerHTML = `
    @media print {
      @page { size: A4; margin: 16mm; }
      body * { visibility: hidden !important; }
      #print-area-wrapper, #print-area-wrapper * { visibility: visible !important; }
      #print-area-wrapper { position: absolute; left: 0; top: 0; width: 100%; }
      .shared-table {
        table-layout: fixed;
        width: 100%;
      }
      .shared-table td {
        width: 50%;
      }
    }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: none; }
  `;

  appendChildren(printWrapper, [root]);
  document.body.appendChild(printStyles);
  document.body.appendChild(printWrapper);

  /** -----------------------------
   * Cleanup after print
   * ------------------------------*/
  const cleanup = () => {
    printWrapper.remove();
    printStyles.remove();
    window.removeEventListener('afterprint', cleanup);
  };

  window.addEventListener('afterprint', cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}
